<template>
  <div class="dashboard">

    <!-- Loading -->
    <div v-if="loading" class="dashboard-loading">
      <i class="ri-loader-4-line ri-spin"></i>
      <p>Cargando dashboard...</p>
    </div>

    <template v-else-if="dashboard">

      <!-- ============================================================= -->
      <!-- CABECERA: saludo + filtro empresa                              -->
      <!-- ============================================================= -->
      <div class="dashboard-header">
        <div class="greeting">
          <h1 class="wz-page-title">{{ greeting }}</h1>
          <p class="wz-page-subtitle">{{ dailyPhrase }}</p>
        </div>
        <div class="company-filter">
          <wz-select
            :items="companyOptions"
            v-model="selectedCompanyId"
            defaultOption="Todas las empresas"
            @change="onCompanyChanged"
          />
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 1: Requiere tu atención                                 -->
      <!-- ============================================================= -->
      <div class="dashboard-section" v-if="hasAlerts">
        <wz-card class="today-card">
          <wz-alert
            v-for="alert in alertItems"
            :key="alert.key"
            :variant="alertVariant(alert.color)"
            :icon="alertIcon(alert.color)"
            class="mb-2"
          >
            <div class="flex items-center justify-between w-full">
              <span><strong>{{ alert.count }}</strong> {{ alert.text }}</span>
              <a href="#" class="alert-action-link" @click.prevent="onAlertClick(alert)">
                Ver <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>
          </wz-alert>
        </wz-card>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 2: Personal activo (4 tarjetas)                         -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <!-- <div class="section-title">
          <i class="ri-team-line"></i>
          Personal activo
        </div> -->

        <div class="indicator-cards">
          <!-- Colaboradores -->
          <wz-card class="stat-card" shadow="sm">
            <div class="stat-card-top">
              <div class="stat-card-info">
                <div class="stat-card-number stat-card-number--blue">{{ indicators.totalEmployees }}</div>
                <div class="stat-card-label">Colaboradores</div>
              </div>
              <div class="stat-card-icon stat-card-icon--blue">
                <i class="ri-team-line"></i>
              </div>
            </div>
            <div class="stat-card-sub" v-if="indicators.newThisMonth > 0">
              <span class="stat-sub-green">+{{ indicators.newThisMonth }}</span> este mes
            </div>
          </wz-card>

          <!-- Contratos vigentes -->
          <wz-card class="stat-card" shadow="sm">
            <div class="stat-card-top">
              <div class="stat-card-info">
                <div class="stat-card-number stat-card-number--orange">{{ indicators.activeContracts }}</div>
                <div class="stat-card-label">Contratos vigentes</div>
              </div>
              <div class="stat-card-icon stat-card-icon--orange">
                <i class="ri-file-list-line"></i>
              </div>
            </div>
            <div class="stat-card-sub" v-if="indicators.noContractCount > 0">
              <span class="stat-sub-red">{{ indicators.noContractCount }}</span> sin contrato
            </div>
          </wz-card>

          <!-- Ingresos -->
          <wz-card class="stat-card" shadow="sm">
            <div class="stat-card-top">
              <div class="stat-card-info">
                <div class="stat-card-number stat-card-number--green">{{ indicators.hiresYear }}</div>
                <div class="stat-card-label">Ingresos ({{ currentYear }})</div>
              </div>
              <div class="stat-card-icon stat-card-icon--green">
                <i class="ri-user-add-line"></i>
              </div>
            </div>
            <div class="stat-card-sub">
              {{ currentMonthShort }}: <span class="stat-sub-green">{{ indicators.hiresMonth }}</span>
            </div>
          </wz-card>

          <!-- Salidas -->
          <wz-card class="stat-card" shadow="sm">
            <div class="stat-card-top">
              <div class="stat-card-info">
                <div :class="['stat-card-number', indicators.exitsYear > 0 ? 'stat-card-number--red' : '']">{{ indicators.exitsYear }}</div>
                <div class="stat-card-label">Salidas ({{ currentYear }})</div>
              </div>
              <div class="stat-card-icon stat-card-icon--red">
                <i class="ri-user-unfollow-line"></i>
              </div>
            </div>
            <div class="stat-card-sub">
              {{ currentMonthShort }}: <span :class="indicators.exitsMonth > 0 ? 'stat-sub-red' : ''">{{ indicators.exitsMonth }}</span>
            </div>
          </wz-card>
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 3: Hoy (ausencias + cumpleaños)                       -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <div class="today-cards">

          <!-- Ausencias de hoy -->
          <wz-card class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Ausencias de hoy</span>
              <wz-badge v-if="absencesToday.length > 0" variant="light" pill>{{ absencesToday.length }}</wz-badge>
            </div>

            <div v-if="absencesToday.length === 0" class="today-empty">
              <i class="ri-checkbox-circle-line" style="color: #28a745"></i>
              <p>Todos los colaboradores están presentes hoy</p>
            </div>

            <ul v-else class="today-list">
              <li v-for="a in absencesVisible" :key="a.employeeId + '-' + a.type">
                <div class="avatar-circle">{{ a.initials }}</div>
                <div class="person-info">
                  <div class="person-name">{{ a.name }}</div>
                  <div class="person-detail">
                    <wz-badge :variant="absenceBadgeVariant(a.type)" pill>
                      {{ a.label || absenceBadgeText(a.type) }}
                    </wz-badge>
                  </div>
                </div>
              </li>
            </ul>

            <a
              v-if="absencesToday.length > 5"
              href="#"
              class="today-more"
              @click.prevent="showAllAbsences = !showAllAbsences"
            >
              {{ showAllAbsences ? 'Ver menos' : 'Ver todas (+' + (absencesToday.length - 5) + ' más)' }}
            </a>
          </wz-card>

          <!-- Cumpleaños del mes -->
          <wz-card class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Cumpleaños — {{ currentMonthName }}</span>
              <wz-badge v-if="birthdays.length > 0" variant="light" pill>{{ birthdays.length }}</wz-badge>
            </div>

            <div v-if="birthdays.length === 0" class="today-empty">
              <i class="ri-cake-line"></i>
              <p>No hay cumpleaños este mes</p>
            </div>

            <ul v-else class="today-list">
              <li
                v-for="b in birthdaysVisible"
                :key="b.employeeId"
                :class="{ 'birthday-today': b.today }"
              >
                <div class="avatar-circle">{{ b.initials }}</div>
                <div class="person-info">
                  <div class="person-name">
                    {{ b.name }}
                    <i v-if="b.today" class="ri-cake-2-line ml-1" style="color: #ffc107"></i>
                  </div>
                  <div class="person-detail">{{ b.birthDate }}</div>
                </div>
              </li>
            </ul>

            <a
              v-if="birthdays.length > 5"
              href="#"
              class="today-more"
              @click.prevent="showAllBirthdays = !showAllBirthdays"
            >
              {{ showAllBirthdays ? 'Ver menos' : 'Ver todos (+' + (birthdays.length - 5) + ' más)' }}
            </a>
          </wz-card>

        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 4: Estado del mes (asistencia + planilla)               -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <!-- <div class="section-title">
          <i class="ri-calendar-check-line"></i>
          Estado del mes
        </div> -->

        <div class="today-cards">

          <!-- Asistencia del mes -->
          <wz-card class="today-card estado-card">
            <div class="today-card-header">
              <span class="today-card-title">Asistencia — {{ currentMonthName }}</span>
            </div>

            <div v-if="!attendance" class="today-empty">
              <i class="ri-calendar-close-line"></i>
              <p>Sin datos de asistencia para este mes</p>
            </div>

            <div v-else class="att-list">
              <!-- Días hábiles -->
              <div class="att-row">
                <span class="att-label">Días hábiles</span>
                <span class="att-value">{{ attendance.workDaysPassed }} de {{ attendance.totalWorkDays }}</span>
              </div>
              <wz-progress :value="workDaysPercent" variant="info" :height="6" class="mb-3" />

              <!-- Faltas -->
              <div class="att-row">
                <span class="att-label">Faltas injustificadas</span>
                <span :class="['att-value', attendance.absences > 0 ? 'text-danger' : 'text-muted']">
                  {{ attendance.absences }}
                </span>
              </div>

              <!-- Tardanzas -->
              <div class="att-row">
                <span class="att-label">Tardanzas registradas</span>
                <span :class="['att-value', attendance.tardiness > 0 ? 'text-warning-dark' : 'text-muted']">
                  {{ attendance.tardiness }}
                </span>
              </div>

              <!-- Horas extra -->
              <div class="att-row">
                <span class="att-label">Horas extra acumuladas</span>
                <span class="att-value text-primary">{{ attendance.overtimeHours || 0 }}h</span>
              </div>

              <!-- Asistencia promedio -->
              <div class="att-row">
                <span class="att-label">Asistencia promedio</span>
                <wz-badge :variant="attendanceRateBadgeVariant">{{ attendance.attendanceRate || 0 }}%</wz-badge>
              </div>
              <wz-progress
                :value="attendance.attendanceRate || 0"
                :variant="attendanceRateVariant"
                :height="6"
                class="mb-3"
              />

              <a href="#" class="today-more mt-1" @click.prevent="$router.push({ name: 'attendance-summary' })">
                Ver detalle <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>
          </wz-card>

          <!-- Planilla del mes -->
          <wz-card class="today-card estado-card">
            <div class="today-card-header">
              <span class="today-card-title">Planilla — {{ currentMonthName }}</span>
            </div>

            <!-- Sin planilla mensual (AUTOCALCULATED + FIFTH) del período -->
            <div v-if="!payroll || !payroll.exists" class="today-empty">
              <i class="ri-file-list-line"></i>
              <p>No hay planilla mensual del mes</p>
              <a href="#" class="today-more" @click.prevent="$router.push({ name: 'payroll-engine' })">
                Crear planilla <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>

            <!-- Con planilla -->
            <div v-else class="payroll-detail">

              <!-- Planilla anulada -->
              <div v-if="payrollAnnulled" class="mb-2">
                <wz-badge variant="danger-solid" pill>ANULADA</wz-badge>
              </div>

              <!-- Stepper de estado -->
              <div class="payroll-stepper">
                <div
                  v-for="(step, idx) in payrollSteps"
                  :key="step.label"
                  :class="['stepper-step', step.state]"
                >
                  <div class="stepper-circle">
                    <i v-if="step.state === 'completed'" class="ri-check-line"></i>
                    <span v-else>{{ idx + 1 }}</span>
                  </div>
                  <div class="stepper-label">{{ step.label }}</div>
                  <div v-if="idx < 3" class="stepper-line" :class="{ completed: step.state === 'completed' }"></div>
                </div>
              </div>

              <!-- Métricas -->
              <div class="payroll-metrics">
                <div class="payroll-metric">
                  <div class="payroll-metric-value payroll-metric-value--primary">{{ payroll.employeeCount }}</div>
                  <div class="payroll-metric-label">Colaboradores</div>
                </div>
                <div class="payroll-metric" v-if="payroll.totalGross">
                  <div class="payroll-metric-value payroll-metric-value--blue">S/ {{ formatNumber(payroll.totalGross) }}</div>
                  <div class="payroll-metric-label">Bruto total</div>
                </div>
                <div class="payroll-metric" v-if="payroll.totalNet">
                  <div class="payroll-metric-value payroll-metric-value--green">S/ {{ formatNumber(payroll.totalNet) }}</div>
                  <div class="payroll-metric-label">Neto total</div>
                </div>
              </div>

              <!-- Meta: última actualización -->
              <div class="payroll-meta" v-if="payroll.lastStatusDate">
                <i class="ri-time-line"></i>
                Actualizado el <span v-local-date="payroll.lastStatusDate"></span>
                <span v-if="payroll.lastStatusUser"> por <strong>{{ payroll.lastStatusUser }}</strong></span>
              </div>

              <a href="#" class="today-more mt-3" @click.prevent="$router.push({ name: 'payroll-engine-detail', params: { id: String(payroll.id) } })">
                Ver planilla <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>

            <!-- Movimientos pendientes: se muestra siempre, exista o no la planilla del mes -->
            <wz-alert
              v-if="payroll && (payroll.pendingMovements || 0) > 0"
              variant="warning"
              class="mt-3 mb-0"
            >
              <div class="flex items-center justify-between w-full">
                <span><strong>{{ payroll.pendingMovements }}</strong> movimientos pendientes</span>
                <a href="#" class="payroll-link" @click.prevent="$router.push({ name: 'payroll-movement-list', query: { fromWidget: 'true', ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}) } })">
                  Ver <i class="ri-arrow-right-s-line"></i>
                </a>
              </div>
            </wz-alert>
          </wz-card>

        </div>
      </div>

    </template>

    <!-- Modal de detalle de alerta (carga lazy) -->
    <wz-modal v-model="alertModal.show" :title="activeModalConfig ? activeModalConfig.title : ''" size="lg">
      <div v-if="alertModal.loading" class="text-center py-4 text-muted">
        <i class="ri-loader-4-line ri-spin"></i> Cargando...
      </div>
      <div v-else-if="alertModal.rows.length === 0" class="text-center py-4 text-muted">
        No hay colaboradores para mostrar.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="wz-table w-full">
          <thead>
            <tr>
              <th v-for="col in activeModalConfig.columns" :key="col.field">{{ col.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in alertModal.rows" :key="i">
              <td v-for="col in activeModalConfig.columns" :key="col.field">
                <router-link
                  v-if="col.link"
                  :to="{ name: 'employee-details', params: { id: row.employeeId }, query: { tab: activeModalConfig.tab } }"
                  class="text-primary"
                >{{ row[col.field] || '-' }}</router-link>
                <span v-else-if="col.date" v-local-date="row[col.field]"></span>
                <wz-badge v-else-if="col.tag === 'days18'" :variant="days18Variant(row.daysUntil18)">
                  {{ days18Text(row.daysUntil18) }}
                </wz-badge>
                <span v-else>{{ row[col.field] || '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #footer>
        <wz-button variant="light" @click="alertModal.show = false">Cerrar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert, Helpers } from "@wizepe/wizeui"
import { DashboardService, CompanyService } from '@/core/services'
import { mapGetters } from 'vuex'

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]
const MONTHS_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

export default {
  name: 'DashboardPage',
  components: {},

  data() {
    return {
      loading: true,
      dashboard: null,
      companies: [],
      selectedCompanyId: null,
      showAllAbsences: false,
      showAllBirthdays: false,
      alertModal: { show: false, type: null, rows: [], loading: false },
      alertModalConfigs: {
        probation: {
          title: 'Colaboradores en período de prueba (próximos 30 días)',
          tab: 'contracts',
          columns: [
            { label: 'Colaborador', field: 'employeeName', link: true },
            { label: 'DNI', field: 'employeeIdentifier' },
            { label: 'Empresa', field: 'companyName' },
            { label: 'Fecha fin de prueba', field: 'probationEndDate', date: true },
          ],
        },
        sinrp: {
          title: 'Colaboradores sin régimen pensionario',
          tab: 'bank',
          columns: [
            { label: 'Colaborador', field: 'employeeName', link: true },
            { label: 'DNI', field: 'employeeIdentifier' },
            { label: 'Empresa', field: 'companyName' },
            { label: 'Área', field: 'areaName' },
          ],
        },
        afpWithoutEntity: {
          title: 'Colaboradores AFP sin entidad pensionaria asignada',
          tab: 'bank',
          columns: [
            { label: 'Colaborador', field: 'employeeName', link: true },
            { label: 'DNI', field: 'employeeIdentifier' },
            { label: 'Empresa', field: 'companyName' },
            { label: 'Área', field: 'areaName' },
          ],
        },
        children18: {
          title: 'Hijos próximos o recientes a cumplir 18 años sin constancia de estudios',
          tab: 'family',
          columns: [
            { label: 'Colaborador', field: 'employeeName', link: true },
            { label: 'DNI', field: 'employeeIdentifier' },
            { label: 'Nombre del hijo', field: 'childName' },
            { label: 'Cumpleaños 18', field: 'daysUntil18', tag: 'days18' },
          ],
        },
        children24: {
          title: 'Hijos por cumplir 24 años',
          tab: 'family',
          columns: [
            { label: 'Colaborador', field: 'employeeName', link: true },
            { label: 'DNI', field: 'employeeIdentifier' },
            { label: 'Nombre del hijo', field: 'childName' },
            { label: 'Cumpleaños 24', field: 'cumpleanos', date: true },
          ],
        },
      },
    }
  },

  computed: {
    ...mapGetters({ user: 'auth/getUser' }),

    activeModalConfig() {
      return this.alertModal.type ? this.alertModalConfigs[this.alertModal.type] : null
    },

    greeting() {
      const hour = new Date().getHours()
      let salute = 'Buenos días'
      if (hour >= 19) salute = 'Buenas noches'
      else if (hour >= 12) salute = 'Buenas tardes'
      const name = this.user ? Helpers.capitalizeName(this.user.name) : ''
      return `${salute}, ${name}`
    },

    dailyPhrase() {
      const phrases = [
        'Tu equipo es tu mayor activo — cuídalo bien.',
        'Cada colaborador feliz es un paso hacia el éxito.',
        'Gestionar personas es construir el futuro de tu empresa.',
        'Un equipo bien gestionado, una empresa bien dirigida.',
        'Las grandes empresas empiezan con grandes equipos.',
      ]
      return phrases[new Date().getDay() % phrases.length]
    },

    todayFormatted() {
      const d = new Date()
      const day = DAYS[d.getDay()]
      const num = d.getDate()
      const month = MONTHS[d.getMonth()]
      const year = d.getFullYear()
      return `${day} ${num} de ${month}, ${year}`
    },

    currentYear() {
      return new Date().getFullYear()
    },

    currentMonthName() {
      return MONTHS[new Date().getMonth()].charAt(0).toUpperCase() + MONTHS[new Date().getMonth()].slice(1)
    },

    currentMonthShort() {
      return MONTHS_SHORT[new Date().getMonth()]
    },

    companyOptions() {
      return this.companies.map(c => ({ key: c.id, label: c.businessName || c.name }))
    },

    alerts() {
      return this.dashboard ? this.dashboard.alerts : {}
    },

    indicators() {
      return this.dashboard ? this.dashboard.indicators : {}
    },

    absencesToday() {
      return this.dashboard ? (this.dashboard.absencesToday || []) : []
    },

    birthdays() {
      return this.dashboard ? (this.dashboard.birthdays || []) : []
    },

    absencesVisible() {
      return this.showAllAbsences ? this.absencesToday : this.absencesToday.slice(0, 5)
    },

    birthdaysVisible() {
      return this.showAllBirthdays ? this.birthdays : this.birthdays.slice(0, 5)
    },

    hasAlerts() {
      const a = this.alerts
      if (!a) return false
      return (a.expiredVacations || 0) > 0
          || (a.pendingVacations || 0) > 0
          || (a.noContract || 0) > 0
          || (a.expiringContracts || 0) > 0
          || (a.expiringVacationPeriods || 0) > 0
          || (a.expiringMedicalLeaves || 0) > 0
          || (a.childrenTurning18 || 0) > 0
          || (a.expiringStudyCertificates || 0) > 0
          || (a.highVacationBalance || 0) > 0
          || (a.probationEnding || 0) > 0
          || (a.upcomingVacations || 0) > 0
          || (a.childrenTurning24 || 0) > 0
          || (a.sinrpEmployees || 0) > 0
          || (a.afpWithoutEntityCount || 0) > 0
          || (a.unsignedContracts || 0) > 0
          || (a.unjustifiedAbsenceAlerts || 0) > 0
          || (a.pendingLeaveDocumentation || 0) > 0
    },

    alertItems() {
      const a = this.alerts
      if (!a) return []
      const items = []

      // --- ROJAS (urgentes) ---
      if (a.expiredVacations > 0) {
        items.push({
          key: 'expiredVacations', color: 'red', count: a.expiredVacations,
          text: 'colaborador(es) con períodos vacacionales vencidos sin gozar',
          route: { name: 'vacation-periods-list', query: { status: 'VENCIDO' } },
        })
      }
      if (a.pendingVacations > 0) {
        items.push({
          key: 'pendingVacations', color: 'red', count: a.pendingVacations,
          text: 'solicitudes de vacaciones pendientes',
          route: { name: 'vacation-requests-list', query: { status: 'PENDIENTE' } },
        })
      }
      if (a.noContract > 0) {
        items.push({
          key: 'noContract', color: 'red', count: a.noContract,
          text: 'colaboradores sin contrato',
          route: { name: 'employee-list', query: { status: 'SIN_CONTRATO' } },
        })
      }
      // --- AMARILLAS (advertencia) ---
      if (a.unjustifiedAbsenceAlerts > 0) {
        items.push({
          key: 'unjustifiedAbsenceAlerts', color: 'yellow', count: a.unjustifiedAbsenceAlerts,
          text: 'colaborador(es) con alertas de inasistencia injustificada (Art. 25, D.L. 728)',
          route: { name: 'attendance-grid', query: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : {} },
        })
      }
      if (a.expiringContracts > 0) {
        items.push({
          key: 'expiringContracts', color: 'yellow', count: a.expiringContracts,
          text: `contratos por vencer (próximos ${a.expiryWarningDays} días)`,
          route: { name: 'contracts-list', query: { status: 'POR_VENCER', ...(this.selectedCompanyId ? { companyId: this.selectedCompanyId } : {}) } },
        })
      }
      if (a.expiringVacationPeriods > 0) {
        items.push({
          key: 'expiringVacationPeriods', color: 'yellow', count: a.expiringVacationPeriods,
          text: 'períodos vacacionales por vencer',
          route: { name: 'vacation-periods-list', query: { status: 'POR_VENCER' } },
        })
      }
      if (a.expiringMedicalLeaves > 0) {
        items.push({
          key: 'expiringMedicalLeaves', color: 'yellow', count: a.expiringMedicalLeaves,
          text: 'descansos médicos por finalizar',
          route: { name: 'medical-leave-list', query: { status: 'ACTIVO' } },
        })
      }
      if (a.pendingLeaveDocumentation > 0) {
        items.push({
          key: 'pendingLeaveDocumentation', color: 'yellow', count: a.pendingLeaveDocumentation,
          text: 'licencia(s) sin documentación (no podrán finalizar hasta adjuntar el sustento)',
          route: { name: 'licenses-list', query: { pendingDoc: 'true', ...(this.selectedCompanyId ? { companyId: this.selectedCompanyId } : {}) } },
        })
      }
      if (a.childrenTurning18 > 0) {
        items.push({
          key: 'childrenTurning18', color: 'yellow', count: a.childrenTurning18,
          text: 'hijo(s) próximos o recientes a cumplir 18 años sin constancia de estudios',
          modal: 'children18',
        })
      }
      if (a.expiringStudyCertificates > 0) {
        items.push({
          key: 'expiringStudyCertificates', color: 'yellow', count: a.expiringStudyCertificates,
          text: 'constancia(s) de estudios por vencer (próximos 30 días)',
          route: { name: 'employee-list' },
        })
      }
      if (a.highVacationBalance > 0) {
        items.push({
          key: 'highVacationBalance', color: 'yellow', count: a.highVacationBalance,
          text: 'colaborador(es) con más de 30 días de vacaciones acumuladas',
          route: { name: 'vacation-periods-list', query: { minPendingDays: 30 } },
        })
      }
      // --- AZULES (info) ---
      if (a.upcomingVacations > 0) {
        items.push({
          key: 'upcomingVacations', color: 'blue', count: a.upcomingVacations,
          text: 'colaborador(es) con vacaciones aprobadas en los próximos 15 días',
          route: { name: 'vacation-requests-list', query: { status: 'APROBADA' } },
        })
      }
      if (a.childrenTurning24 > 0) {
        items.push({
          key: 'childrenTurning24', color: 'blue', count: a.childrenTurning24,
          text: 'hijo(s) próximo(s) a cumplir 24 años (fin de asignación familiar)',
          modal: 'children24',
        })
      }
      if (a.probationEnding > 0) {
        items.push({
          key: 'probationEnding', color: 'blue', count: a.probationEnding,
          text: 'colaboradores en período de prueba (próximos 30 días)',
          modal: 'probation',
        })
      }
      // --- AMARILLAS (advertencia) continuación ---
      if (a.sinrpEmployees > 0) {
        items.push({
          key: 'sinrpEmployees', color: 'yellow', count: a.sinrpEmployees,
          text: 'colaborador(es) sin régimen pensionario asignado',
          modal: 'sinrp',
        })
      }
      if (a.afpWithoutEntityCount > 0) {
        items.push({
          key: 'afpWithoutEntityCount', color: 'yellow', count: a.afpWithoutEntityCount,
          text: 'colaborador(es) AFP sin entidad pensionaria asignada',
          modal: 'afpWithoutEntity',
        })
      }
      if (a.unsignedContracts > 0) {
        items.push({
          key: 'unsignedContracts', color: 'yellow', count: a.unsignedContracts,
          text: 'contrato(s) activo(s) sin firma',
          route: { name: 'contracts-list', query: { status: 'VIGENTE', hasSignedDocument: false, excludeNotStarted: true, ...(this.selectedCompanyId ? { companyId: this.selectedCompanyId } : {}) } },
        })
      }

      return items
    },

    // Bloque 4
    attendance() {
      return this.dashboard ? this.dashboard.attendance : null
    },

    payroll() {
      return this.dashboard ? this.dashboard.payroll : null
    },

    workDaysPercent() {
      const a = this.attendance
      if (!a || !a.totalWorkDays) return 0
      return Math.round((a.workDaysPassed / a.totalWorkDays) * 100)
    },

    attendanceRateBadgeVariant() {
      const rate = this.attendance ? (this.attendance.attendanceRate || 0) : 0
      if (rate >= 95) return 'success'
      if (rate >= 90) return 'warning'
      return 'danger'
    },

    attendanceRateVariant() {
      const rate = this.attendance ? (this.attendance.attendanceRate || 0) : 0
      if (rate >= 95) return 'success'
      if (rate >= 90) return 'warning'
      return 'danger'
    },

    // Estados del backend en inglés (DRAFT/CALCULATED/APPROVED/PAID/ANNULLED); labels en español.
    payrollSteps() {
      const status = this.payroll ? (this.payroll.status || 'DRAFT') : null
      const steps = [
        { label: 'Borrador', key: 'DRAFT' },
        { label: 'Calculada', key: 'CALCULATED' },
        { label: 'Aprobada', key: 'APPROVED' },
        { label: 'Pagada', key: 'PAID' },
      ]

      // Anulada: estado terminal fuera del flujo → todos los pasos atenuados.
      if (status === 'ANNULLED') {
        return steps.map(s => ({ ...s, state: 'annulled' }))
      }

      const statusOrder = ['DRAFT', 'CALCULATED', 'APPROVED', 'PAID']
      const statusIdx = statusOrder.indexOf(status)

      return steps.map((s, i) => {
        if (statusIdx < 0) return { ...s, state: 'pending' }
        if (i < statusIdx) return { ...s, state: 'completed' }
        if (i === statusIdx) return { ...s, state: 'active' }
        return { ...s, state: 'pending' }
      })
    },
    payrollAnnulled() {
      return !!this.payroll && this.payroll.status === 'ANNULLED'
    },
  },

  methods: {
    alertVariant(color) {
      return { red: 'danger', yellow: 'warning', blue: 'info' }[color] || 'primary'
    },
    alertIcon(color) {
      return { red: 'ri-error-warning-line', yellow: 'ri-alert-line', blue: 'ri-information-line' }[color] || 'ri-information-line'
    },
    formatNumber(val) {
      if (val == null) return '0.00'
      return Number(val).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    // Color del badge de "Ausencias de hoy" según el tipo de ausencia.
    absenceBadgeVariant(type) {
      if (type === 'VACATION') return 'info'
      if (type === 'MEDICAL_LEAVE') return 'warning'
      return 'primary' // LEAVE (licencias/permisos) u otros
    },
    // Texto de respaldo si el backend no envía label (compatibilidad).
    absenceBadgeText(type) {
      if (type === 'VACATION') return 'Vacaciones'
      if (type === 'MEDICAL_LEAVE') return 'Desc. médico'
      return 'Licencia'
    },

    async loadDashboard() {
      try {
        this.loading = true
        const resp = await DashboardService.getDashboard(this.selectedCompanyId)
        this.dashboard = resp.payload
      } catch (e) {
        console.error('Error al cargar dashboard', e)
      } finally {
        this.loading = false
      }
    },

    onAlertClick(alert) {
      if (alert.modal) this.openAlertModal(alert.modal)
      else if (alert.route) this.$router.push(alert.route)
    },
    async openAlertModal(type) {
      this.alertModal.type = type
      this.alertModal.rows = []
      this.alertModal.loading = true
      this.alertModal.show = true
      try {
        const resp = await this.fetchAlertDetail(type, this.selectedCompanyId)
        this.alertModal.rows = resp.payload || []
      } catch (e) {
        Alert.error('Error al cargar el detalle de la alerta')
      } finally {
        this.alertModal.loading = false
      }
    },
    fetchAlertDetail(type, companyId) {
      switch (type) {
        case 'probation':  return DashboardService.getProbationAlerts(companyId)
        case 'sinrp':      return DashboardService.getSinrpAlerts(companyId)
        case 'afpWithoutEntity': return DashboardService.getAfpWithoutEntityAlerts(companyId)
        case 'children18': return DashboardService.getChildren18Alerts(companyId)
        case 'children24': return DashboardService.getChildren24Alerts(companyId)
        default:           return Promise.resolve({ payload: [] })
      }
    },
    days18Variant(d) {
      return d < 0 ? 'warning-solid' : 'info-solid'
    },
    days18Text(d) {
      return d < 0 ? `Cumplió hace ${Math.abs(d)} días` : `Cumple en ${d} días`
    },

    onCompanyChanged(val) {
      this.selectedCompanyId = val || null
      if (this.selectedCompanyId) {
        sessionStorage.setItem('dashboard_companyId', this.selectedCompanyId)
      } else {
        sessionStorage.removeItem('dashboard_companyId')
      }
      this.loadDashboard()
    },
  },

  async created() {
    const saved = sessionStorage.getItem('dashboard_companyId')
    if (saved) this.selectedCompanyId = Number(saved)

    try {
      const resp = await CompanyService.listAll()
      this.companies = resp.payload || []
    } catch (e) {
      console.error('Error al cargar empresas', e)
    }

    this.loadDashboard()
  },
}
</script>

<style lang="stylus">
@import "../Styles/Dashboard.styl"
</style>
